import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  ComposedChart, LineChart, Line, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, Brush, Area, ReferenceLine,
} from "recharts";
import { useTheme } from "../common/ThemeContext";
import { fetchAuth } from '../common/api';
import { fetchAlerts } from "../common/alertsApi";
import { motion } from "framer-motion";
import { useSeason, getSeasonRange } from "../common/SeasonContext";
import PlayerPicker from "../common/PlayerPicker";
import SideControls from "../common/SideControls";
import {
  buildActiveAlertPlayerIdSet,
  isPlayerAtRisk,
  PLAYER_AT_RISK_TRAINABILITY_THRESHOLD,
} from "../common/playerRiskUtils";
import { useTranslation } from 'react-i18next'

const TEAM_PERFORMANCE_REF_ENDPOINT = "/reports/team-performance/reference-config";
const CHART_TRAINABILITY_AT_RISK_THRESHOLD = PLAYER_AT_RISK_TRAINABILITY_THRESHOLD;
const CHART_RECENT_RISK_WINDOW_DAYS = 7;
const CHART_ALERTS_REFRESH_INTERVAL_MS = 60 * 1000;

function parseIsoOrSessionDay(value) {
  if (!value) return null;
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value;
  const raw = String(value).trim();
  if (!raw) return null;

  if (raw.includes("/")) {
    const [day, month, year] = raw.split("/");
    const dt = new Date(Number(year), Number(month) - 1, Number(day), 12, 0, 0, 0);
    return Number.isNaN(dt.getTime()) ? null : dt;
  }

  const dt = new Date(raw.length === 10 ? `${raw}T12:00:00` : raw);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

function startOfDay(value) {
  const dt = value instanceof Date ? new Date(value) : parseIsoOrSessionDay(value);
  if (!dt || Number.isNaN(dt.getTime())) return null;
  dt.setHours(0, 0, 0, 0);
  return dt;
}

function formatRiskDate(value, locale, options) {
  if (!(value instanceof Date) || Number.isNaN(value.getTime())) return "";
  return value.toLocaleDateString(locale || "fr-FR", options);
}

function formatIsoDate(value) {
  const dt = startOfDay(value);
  if (!dt) return null;
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, "0");
  const d = String(dt.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function resolvePeriodRange({ period, selectedDate, season, startDate, endDate }) {
  if (!selectedDate || String(selectedDate).trim().toUpperCase() === "N/A") return null;

  const referenceEnd = startOfDay(parseIsoOrSessionDay(selectedDate));
  if (!referenceEnd || !season) return null;

  const { start: seasonStart, end: seasonEnd } = getSeasonRange(season);
  const seasonStartDay = startOfDay(seasonStart);
  const seasonEndDay = startOfDay(seasonEnd);
  if (!seasonStartDay || !seasonEndDay) return null;

  const clampToSeason = (rawStart, rawEnd) => {
    const start = new Date(Math.max(rawStart.getTime(), seasonStartDay.getTime()));
    const end = new Date(
      Math.min(rawEnd.getTime(), seasonEndDay.getTime(), referenceEnd.getTime())
    );
    if (start.getTime() > end.getTime()) return null;
    return { start, end };
  };

  if (period === "week") {
    const start = new Date(referenceEnd);
    start.setDate(start.getDate() - 6);
    return clampToSeason(start, referenceEnd);
  }

  if (period === "month") {
    const start = new Date(referenceEnd);
    start.setDate(start.getDate() - 30);
    return clampToSeason(start, referenceEnd);
  }

  if (period === "season") {
    return clampToSeason(seasonStartDay, referenceEnd);
  }

  if (period === "custom") {
    const customStart = startOfDay(parseIsoOrSessionDay(startDate));
    const customEnd = startOfDay(parseIsoOrSessionDay(endDate));
    if (!customStart || !customEnd) return null;
    return clampToSeason(customStart, customEnd);
  }

  return null;
}

function filterSessionsInRange(sessions, range) {
  if (!range || !Array.isArray(sessions)) return sessions || [];

  const startMs = startOfDay(range.start)?.getTime();
  const endMs = startOfDay(range.end)?.getTime();
  if (!startMs || !endMs) return sessions;

  return sessions.filter((session) => {
    const day = startOfDay(parseIsoOrSessionDay(session?.day));
    if (!day) return false;
    const ms = day.getTime();
    return ms >= startMs && ms <= endMs;
  });
}

function buildChartDateAxis(range) {
  if (!range?.start || !range?.end) return [];

  const fmt = (d) =>
    `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;

  const cur = startOfDay(range.start);
  const end = startOfDay(range.end);
  if (!cur || !end || cur.getTime() > end.getTime()) return [];

  const dates = [];
  while (cur.getTime() <= end.getTime()) {
    dates.push(fmt(cur));
    cur.setDate(cur.getDate() + 1);
  }
  return dates;
}

function parseNullableReference(value) {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

function formatReferenceDisplay(value, step = 0.01) {
  if (!Number.isFinite(value)) return "";
  const decimals = step >= 0.1 ? 1 : 2;
  return value.toFixed(decimals);
}

function CompactThreshold({
  range,
  setRange,
  min,
  max,
  step = 1,
  unit,
  mode,
  setMode,
  isDarkMode,
  subLabel,
  referenceEditor = null,
}) {
   const { t } = useTranslation()
  const [localMin, localMax] = range;
  const setLower = (v) => setRange(Number(v) > localMax ? [localMax, Number(v)] : [Number(v), localMax]);
  const setUpper = (v) => setRange(Number(v) < localMin ? [Number(v), localMin] : [localMin, Number(v)]);

  const segBtn = (key, label) => {
    const active = mode === key;
    return (
      <button
        type="button"
        onClick={() => setMode(key)}
        className={`text-xs px-2.5 py-1 rounded-md transition
          ${active ? "bg-indigo-600 text-white" :
            (isDarkMode ? "bg-gray-800 text-gray-200 hover:bg-gray-700"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200")}`}
      >
        {label}
      </button>
    );
  };

  return (
    <div className="w-full h-full flex flex-col">
      <div className="flex items-center justify-end mb-2">
        <div className="inline-flex gap-1 p-1 rounded-lg">
     {segBtn("number", t('charts.mode_number'))}
  {segBtn("distance", t('charts.mode_distance'))}
  {segBtn("time", t('charts.mode_time'))}
        </div>
      </div>

      {/* xl+: sliders verticaux | <xl: horizontaux */}
      <div className="flex-1 flex items-center justify-center gap-6">
        {/* Upper */}
        <div className="flex flex-col xl:items-center">
          <span className="text-[10px] uppercase tracking-wide opacity-70 mb-1 text-center xl:text-inherit">Upper</span>
          {/* vertical (xl+) */}
          <input
            type="range"
            min={min} max={max} step={step}
            value={localMax} onChange={(e)=>setUpper(e.target.value)}
            className="hidden xl:block vrange"
            style={{ writingMode: "vertical-lr", direction: "rtl", height: 120 }}
          />
          {/* horizontal (<xl) */}
          <input
            type="range"
            min={min} max={max} step={step}
            value={localMax} onChange={(e)=>setUpper(e.target.value)}
            className="block xl:hidden hrange"
            style={{ width: 140 }}
          />
          <span className="text-xs mt-1 text-center">{localMax} {unit}</span>
        </div>

        {/* Lower */}
        <div className="flex flex-col xl:items-center">
          <span className="text-[10px] uppercase tracking-wide opacity-70 mb-1 text-center xl:text-inherit">Lower</span>
          <input
            type="range"
            min={min} max={max} step={step}
            value={localMin} onChange={(e)=>setLower(e.target.value)}
            className="hidden xl:block vrange"
            style={{ writingMode: "vertical-lr", direction: "rtl", height: 120 }}
          />
          <input
            type="range"
            min={min} max={max} step={step}
            value={localMin} onChange={(e)=>setLower(e.target.value)}
            className="block xl:hidden hrange"
            style={{ width: 140 }}
          />
          <span className="text-xs mt-1 text-center">{localMin} {unit}</span>
        </div>
      </div>

      <div className="mt-2 text-center text-xs opacity-80">
        [{localMin} – {localMax}] {unit}
      </div>
      {subLabel && (
        <div className="mt-1 text-center text-[11px] opacity-70 leading-snug">
          {subLabel}
        </div>
      )}

      {referenceEditor && (
        <div
          className={`mt-3 rounded-lg border p-2 ${
            isDarkMode
              ? "border-gray-700 bg-gray-900/70"
              : "border-gray-200 bg-white"
          }`}
        >
          <div className="flex items-center justify-between gap-2 text-[11px]">
            <span className="opacity-80">
              {t("charts.reference_100")}:
              {" "}
              <span className="font-semibold">
                {formatReferenceDisplay(referenceEditor.currentValue, referenceEditor.step)}{" "}
                {referenceEditor.unit}
              </span>
            </span>
            <span
              className={`rounded px-1.5 py-0.5 text-[10px] ${
                referenceEditor.isManual
                  ? isDarkMode
                    ? "bg-indigo-500/20 text-indigo-300"
                    : "bg-indigo-100 text-indigo-700"
                  : isDarkMode
                    ? "bg-gray-800 text-gray-300"
                    : "bg-gray-100 text-gray-600"
              }`}
            >
              {referenceEditor.isManual ? t("charts.reference_manual") : t("charts.reference_auto")}
            </span>
          </div>

          <div className="mt-2 flex items-center gap-2">
            <input
              type="number"
              min="0"
              step={referenceEditor.step || "0.01"}
              value={
                referenceEditor.draftValue !== ""
                  ? referenceEditor.draftValue
                  : referenceEditor.isManual
                    ? ""
                    : formatReferenceDisplay(
                        referenceEditor.currentValue,
                        referenceEditor.step
                      )
              }
              onChange={(event) => referenceEditor.onDraftChange(event.target.value)}
              placeholder={formatReferenceDisplay(
                referenceEditor.currentValue,
                referenceEditor.step
              )}
              className={`w-24 rounded border px-2 py-1 text-xs ${
                isDarkMode
                  ? "border-gray-600 bg-gray-800 text-gray-100"
                  : "border-gray-300 bg-white text-gray-800"
              }`}
            />

            <button
              type="button"
              onClick={referenceEditor.onSave}
              disabled={referenceEditor.saving}
              className={`rounded px-2 py-1 text-xs font-medium transition ${
                referenceEditor.saving
                  ? "cursor-not-allowed opacity-60"
                  : "hover:opacity-90"
              } ${
                isDarkMode
                  ? "bg-indigo-600 text-white"
                  : "bg-indigo-500 text-white"
              }`}
            >
              {t("charts.reference_save")}
            </button>
            <button
              type="button"
              onClick={referenceEditor.onReset}
              disabled={referenceEditor.saving}
              className={`rounded border px-2 py-1 text-xs ${
                isDarkMode
                  ? "border-gray-600 text-gray-200 hover:bg-gray-800"
                  : "border-gray-300 text-gray-700 hover:bg-gray-100"
              }`}
            >
              {t("charts.reference_reset")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function TeamPerformanceCharts({
  selectedPosition,
  selectedDate,
  category,
  restrictedInternalMetrics = false,
  atRiskChartsOnly = false,
  overviewLoading = false,
}) {
  const { t, i18n } = useTranslation()
  const [weeklyData, setWeeklyData] = useState([]);
  const [filteredPlayers, setFilteredPlayers] = useState([]);
  const { isDarkMode } = useTheme();
  const [activeTab, setActiveTab] = useState(null);
  const [activeAlertPlayerIds, setActiveAlertPlayerIds] = useState(() => new Set());

  // 🔹 Threshold states (now as RANGE [min, max])
  const [accelPctRange, setAccelPctRange] = useState([30, 80]);
  const [decelPctRange, setDecelPctRange] = useState([30, 80]);
  const [sprintPctRange, setSprintPctRange] = useState([80, 100]);

  const [cumulative, setCumulative] = useState(false);
  const [period, setPeriod] = useState("week"); // week | month | season | custom

  // Custom period
  const todayIso = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const [startDate, setStartDate] = useState(""); // "YYYY-MM-DD"
  const [endDate, setEndDate] = useState("");

  const [activePlayers, setActivePlayers] = useState({});
  const { season } = useSeason();
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState("distance"); // "number" | "distance" | "time"
  const [displayMode, setDisplayMode] = useState("curve");
  const [useMD60, setUseMD60] = useState(false);
  const [trainabilityPeriod, setTrainabilityPeriod] = useState("30"); // "30" | "60"
  const [trainabilityScope, setTrainabilityScope] = useState("global");

  // Match days from calendar
  const [matchDates, setMatchDates] = useState([]); // [{ date: "DD/MM/YYYY", opponent: "..." }, ...]
  const [referenceOverrides, setReferenceOverrides] = useState({
    team_vma_max: null,
    team_accel_max: null,
    team_decel_mag_max: null,
  });
  const [referenceDrafts, setReferenceDrafts] = useState({
    team_vma_max: "",
    team_accel_max: "",
    team_decel_mag_max: "",
  });
  const [referenceSaveKey, setReferenceSaveKey] = useState("");
  const [referenceError, setReferenceError] = useState("");

  useEffect(() => {
    if (!season) return;
    const fetchMatchDates = async () => {
      try {
        const res = await fetchAuth(`/calendar?season=${season}`);
        if (!res.ok) return;
        const cal = await res.json();
        const events = cal.events || {};
        const matches = [];
        for (const [dateKey, dayObj] of Object.entries(events)) {
          for (const period of ["morning", "afternoon"]) {
            const ev = dayObj?.[period];
            if (!ev) continue;
            const evList = Array.isArray(ev) ? ev : [ev];
            for (const e of evList) {
              if (e?.type === "match") {
                const [y, m, d] = dateKey.split("-");
                matches.push({
                  date: `${d}/${m}/${y}`,
                  opponent: e.opponent?.name || "",
                });
              }
            }
          }
        }
        setMatchDates(matches);
      } catch { /* silent */ }
    };
    fetchMatchDates();
  }, [season]);

  const matchDateSet = useMemo(
    () => new Map(matchDates.map(m => [m.date, m.opponent])),
    [matchDates]
  );

  const periodRange = useMemo(
    () => resolvePeriodRange({ period, selectedDate, season, startDate, endDate }),
    [period, selectedDate, season, startDate, endDate]
  );

  const periodRangeKey = useMemo(() => {
    if (!periodRange) return null;
    return `${period}|${formatIsoDate(periodRange.start)}|${formatIsoDate(periodRange.end)}`;
  }, [period, periodRange]);

  const weeklyHistoryFetchSeqRef = useRef(0);

  const chartReferenceDate = useMemo(() => {
    if (periodRange?.end) return periodRange.end;

    if (period === "custom" && endDate) {
      const customEnd = parseIsoOrSessionDay(endDate);
      if (customEnd) return customEnd;
    }

    const selected = parseIsoOrSessionDay(selectedDate);
    return selected || new Date();
  }, [periodRange, period, endDate, selectedDate]);

  useEffect(() => {
    let cancelled = false;

    const loadActiveAlerts = async () => {
      try {
        const { alerts } = await fetchAlerts({ status: "all", limit: 200, offset: 0 });
        if (!cancelled) {
          setActiveAlertPlayerIds(buildActiveAlertPlayerIdSet(alerts));
        }
      } catch {
        // Keep existing alert-derived set if alerts endpoint is unavailable.
      }
    };

    loadActiveAlerts();
    const intervalId = window.setInterval(loadActiveAlerts, CHART_ALERTS_REFRESH_INTERVAL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, []);

  const riskProfilesByPlayerId = useMemo(() => {
    const profiles = new Map();
    const referenceDay = startOfDay(chartReferenceDate);
    if (!referenceDay) return profiles;

    const recentWindowMs = CHART_RECENT_RISK_WINDOW_DAYS * 24 * 60 * 60 * 1000;

    (weeklyData || []).forEach((player) => {
      const playerId = String(player?.id ?? "");
      if (!playerId) return;

      const sessions = (player.weeklyHistory || [])
        .map((session) => ({ ...session, __date: parseIsoOrSessionDay(session.day) }))
        .filter((session) => session.__date && !Number.isNaN(session.__date.getTime()))
        .sort((a, b) => a.__date - b.__date);

      const latestSession = sessions.length > 0 ? sessions[sessions.length - 1] : null;
      const latestTrainabilityRaw = Number(latestSession?.trainability);
      const latestTrainability = Number.isFinite(latestTrainabilityRaw) ? latestTrainabilityRaw : null;

      const currentAtRisk = isPlayerAtRisk(
        {
          id: playerId,
          player_id: playerId,
          trainability_index: latestTrainability,
        },
        {
          activeAlertPlayerIds,
          trainabilityThreshold: CHART_TRAINABILITY_AT_RISK_THRESHOLD,
        }
      );

      const recentRiskSessions = sessions.filter((session) => {
        const sessionDay = startOfDay(session.__date);
        if (!sessionDay) return false;
        const ageMs = referenceDay.getTime() - sessionDay.getTime();
        if (ageMs < 0 || ageMs >= recentWindowMs) return false;
        const trainability = Number(session.trainability);
        return Number.isFinite(trainability) && trainability < CHART_TRAINABILITY_AT_RISK_THRESHOLD;
      });

      const hadRecentRisk = recentRiskSessions.length > 0;

      let monitoredSince = null;
      if (!currentAtRisk && hadRecentRisk) {
        const lastRiskDate = recentRiskSessions[recentRiskSessions.length - 1].__date;
        const firstSafeAfterRisk = sessions.find((session) => {
          if (session.__date <= lastRiskDate) return false;
          const trainability = Number(session.trainability);
          return Number.isFinite(trainability) && trainability >= CHART_TRAINABILITY_AT_RISK_THRESHOLD;
        });

        monitoredSince = firstSafeAfterRisk ? firstSafeAfterRisk.__date : lastRiskDate;
      }

      profiles.set(playerId, {
        isIncluded: currentAtRisk || hadRecentRisk,
        currentAtRisk,
        hadRecentRisk,
        monitoredSince,
      });
    });

    return profiles;
  }, [weeklyData, activeAlertPlayerIds, chartReferenceDate]);

  useEffect(() => {
    let cancelled = false;

    const fetchReferenceConfig = async () => {
      try {
        const response = await fetchAuth(TEAM_PERFORMANCE_REF_ENDPOINT);
        if (!response.ok) return;
        const payload = await response.json();
        if (cancelled) return;

        const refs = payload?.references || {};
        setReferenceOverrides({
          team_vma_max: parseNullableReference(refs.team_vma_max),
          team_accel_max: parseNullableReference(refs.team_accel_max),
          team_decel_mag_max: parseNullableReference(refs.team_decel_mag_max),
        });
        setReferenceDrafts({
          team_vma_max: refs.team_vma_max ?? "",
          team_accel_max: refs.team_accel_max ?? "",
          team_decel_mag_max: refs.team_decel_mag_max ?? "",
        });
        setReferenceError("");
      } catch {
        if (!cancelled) {
          setReferenceError(t("charts.reference_load_error"));
        }
      }
    };

    fetchReferenceConfig();
    return () => {
      cancelled = true;
    };
  }, [t]);

  const allMetrics = useMemo(() => {
    const internalMetrics = [
      { key: "trainability", label: t('metrics.trainability_label'), unit: "" },
      { key: "microcycle", label: t('metrics.microcycle_label'), unit: "" },
      { key: "fatigue", label: t('metrics.fatigue_label'), unit: "%" },
      { key: "intensity_index", label: t('metrics.intensity_label'), unit: "PT" },
      { key: "mechanical_load_index", label: t('metrics.mech_load_label'), unit: "PT" },
      { key: "power", label: t('metrics.power_label'), unit: "w/kg" },
    ];
    const metricsByCategory = {
      internal: restrictedInternalMetrics
        ? internalMetrics.filter(({ key }) => ["trainability", "fatigue"].includes(key))
        : internalMetrics,
      external: [
        { key: "acceleration_threshold", label: t('metrics.accelerations'), unit: "" },
        { key: "deceleration_threshold", label: t('metrics.decelerations'), unit: "" },
        { key: "sprint_threshold", label: t('metrics.sprints'), unit: "" },
        { key: "accelerations", label: t('metrics.accel_max_label'), unit: "m/s²" },
        { key: "decelerations", label: t('metrics.decel_max_label'), unit: "m/s²" },
        { key: "sprint_max", label: t('metrics.speed_max_label'), unit: "km/h" },
        { key: "global_distance", label: t('metrics.total_distance_label'), unit: "km" },
      ],
    };
    const extraMetrics = [];
    return [...(metricsByCategory[category] || []), ...extraMetrics];
  }, [category, restrictedInternalMetrics, t]);
  const hasReferenceDate =
    !!selectedDate && String(selectedDate).trim().toUpperCase() !== "N/A";
  const isSyncingFilteredPlayers = weeklyData.length > 0 && filteredPlayers.length === 0;
  const showLoading =
    loading || overviewLoading || !hasReferenceDate || isSyncingFilteredPlayers;

  const hasData = filteredPlayers.some(p => p.weeklyHistory?.length);

  // Fetch history for the resolved period (explicit date range, aligned with season context)
  useEffect(() => {
    if (!periodRange || !periodRangeKey) {
      setWeeklyData([]);
      setLoading(overviewLoading || !hasReferenceDate);
      return;
    }

    setLoading(true);
    setWeeklyData([]);

    const fetchSeq = ++weeklyHistoryFetchSeqRef.current;
    const controller = new AbortController();

    const fetchWeeklyData = async () => {
      try {
        const params = new URLSearchParams({
          period: "custom",
          start_date: formatIsoDate(periodRange.start),
          end_date: formatIsoDate(periodRange.end),
        });
        const response = await fetchAuth(`/players_weekly_history?${params.toString()}`, {
          signal: controller.signal,
        });
        if (fetchSeq !== weeklyHistoryFetchSeqRef.current) return;
        if (!response.ok) throw new Error("Failed to fetch data");
        const data = await response.json();
        if (fetchSeq !== weeklyHistoryFetchSeqRef.current) return;

        const normalized = (Array.isArray(data) ? data : []).map((player) => ({
          ...player,
          weeklyHistory: filterSessionsInRange(player.weeklyHistory, periodRange),
        }));
        setWeeklyData(normalized);
      } catch (error) {
        if (controller.signal.aborted || fetchSeq !== weeklyHistoryFetchSeqRef.current) return;
        setWeeklyData([]);
      } finally {
        if (fetchSeq === weeklyHistoryFetchSeqRef.current) {
          setLoading(false);
        }
      }
    };

    fetchWeeklyData();

    return () => {
      controller.abort();
    };
  }, [periodRange, periodRangeKey, overviewLoading, hasReferenceDate]);

  // Position filtering + date filtering if custom
  useEffect(() => {
    if (!weeklyData.length) {
      setFilteredPlayers([]);
      setActivePlayers({});
      return;
    }

    // 1. Filter by position
    let updatedPlayers =
      selectedPosition === "All"
        ? weeklyData
        : weeklyData.filter((player) => player.position === selectedPosition);

    if (periodRange) {
      updatedPlayers = updatedPlayers.map((player) => ({
        ...player,
        weeklyHistory: filterSessionsInRange(player.weeklyHistory, periodRange),
      }));
    }

    // 2. At-risk filter (overview at-risk mode only)
    if (atRiskChartsOnly) {
      updatedPlayers = updatedPlayers.filter((player) => {
        const profile = riskProfilesByPlayerId.get(String(player.id));
        return !!profile?.isIncluded;
      });
    }

    // 3. Update the filtered list
    setFilteredPlayers(updatedPlayers);

    // 4. Init selection
    const initialActivePlayers = {};

    updatedPlayers.forEach((player) => {
      const name = `${player.first_name} ${player.last_name}`;
      initialActivePlayers[name] = true;
    });

    setActivePlayers(initialActivePlayers);
  }, [weeklyData, selectedPosition, atRiskChartsOnly, riskProfilesByPlayerId, periodRange]);

  const playerRiskStatusByName = useMemo(() => {
    const statuses = {};
    const locale = i18n?.language || "fr-FR";

    filteredPlayers.forEach((player) => {
      const playerName = `${player.first_name} ${player.last_name}`;
      const profile = riskProfilesByPlayerId.get(String(player.id));
      if (!profile) return;

      if (profile.currentAtRisk) {
        statuses[playerName] = { kind: "at-risk" };
        return;
      }

      if (profile.hadRecentRisk) {
        statuses[playerName] = {
          kind: "monitored",
          sinceShort: formatRiskDate(profile.monitoredSince, locale, {
            day: "2-digit",
            month: "2-digit",
          }),
          sinceFull: formatRiskDate(profile.monitoredSince, locale, {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
          }),
        };
      }
    });

    return statuses;
  }, [filteredPlayers, riskProfilesByPlayerId, i18n?.language]);

  const teamRefs = useMemo(() => {
    // VMA max (km/h)
    const vmas = (weeklyData || [])
      .map(p => p?.vma_month)//.map(p => p?.vma) vma_week vma_month vma_year
      .filter(v => typeof v === "number" && !Number.isNaN(v));
    const teamVmaMax = vmas.length ? Math.max(...vmas) : null;

    // accel max (m/s²) based on "accelerations" values (acceleration_max)
    const accelVals = (weeklyData || []).flatMap(p =>
      (p.weeklyHistory || [])
        .map(s => s?.accelerations)
        .filter(v => typeof v === "number" && !Number.isNaN(v))
    );
    const teamAccelMax = accelVals.length ? Math.max(...accelVals) : null;

    // decel max magnitude (m/s²) based on "decelerations" (negative values)
    const decelVals = (weeklyData || []).flatMap(p =>
      (p.weeklyHistory || [])
        .map(s => s?.decelerations)
        .filter(v => typeof v === "number" && !Number.isNaN(v))
    );
    const teamDecelMagMax = decelVals.length ? Math.max(...decelVals.map(v => Math.abs(v))) : null;

    return {
      teamVmaMax: teamVmaMax ?? 30,         // reasonable fallback
      teamAccelMax: teamAccelMax ?? 6,      // fallback
      teamDecelMagMax: teamDecelMagMax ?? 6 // fallback
    };
  }, [weeklyData]);

  const effectiveTeamRefs = useMemo(
    () => ({
      teamVmaMax:
        parseNullableReference(referenceOverrides.team_vma_max) ?? teamRefs.teamVmaMax,
      teamAccelMax:
        parseNullableReference(referenceOverrides.team_accel_max) ?? teamRefs.teamAccelMax,
      teamDecelMagMax:
        parseNullableReference(referenceOverrides.team_decel_mag_max) ?? teamRefs.teamDecelMagMax,
    }),
    [teamRefs, referenceOverrides]
  );

  const updateReferenceDraft = (key, value) => {
    setReferenceDrafts((prev) => ({ ...prev, [key]: value }));
  };

  const persistReference = async (key, rawValue) => {
    setReferenceSaveKey(key);
    setReferenceError("");
    try {
      const response = await fetchAuth(TEAM_PERFORMANCE_REF_ENDPOINT, {
        method: "PATCH",
        body: JSON.stringify({ references: { [key]: rawValue } }),
      });
      if (!response.ok) {
        const details = await response.json().catch(() => ({}));
        throw new Error(details?.error || details?.message || "save_failed");
      }
      const payload = await response.json().catch(() => ({}));
      const refs = payload?.references || {};
      setReferenceOverrides({
        team_vma_max: parseNullableReference(refs.team_vma_max),
        team_accel_max: parseNullableReference(refs.team_accel_max),
        team_decel_mag_max: parseNullableReference(refs.team_decel_mag_max),
      });
      setReferenceDrafts((prev) => ({
        ...prev,
        [key]: refs[key] ?? "",
      }));
    } catch {
      setReferenceError(t("charts.reference_save_error"));
    } finally {
      setReferenceSaveKey("");
    }
  };

  const saveReference = async (key) => {
    const rawDraft = referenceDrafts[key];
    const parsed = Number(rawDraft);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      setReferenceError(t("charts.reference_validation_error"));
      return;
    }
    await persistReference(key, parsed);
  };

  const resetReference = async (key) => {
    await persistReference(key, null);
  };

  const referenceMetaByMetric = useMemo(
    () => ({
      sprint_threshold: {
        referenceKey: "team_vma_max",
        unit: "km/h",
        step: 0.1,
      },
      acceleration_threshold: {
        referenceKey: "team_accel_max",
        unit: "m/s²",
        step: 0.01,
      },
      deceleration_threshold: {
        referenceKey: "team_decel_mag_max",
        unit: "m/s²",
        step: 0.01,
      },
    }),
    []
  );

  useEffect(() => {
    setActiveTab((current) =>
      allMetrics.some((metric) => metric.key === current)
        ? current
        : allMetrics.length > 0
        ? allMetrics[0].key
        : null
    );
  }, [allMetrics]);

  const DASHES = ["", "6 4", "3 3", "10 5 2 5"];

  const buildPalette = (count, isDark) => {
    const s = 70;                          // saturation
    const l1 = isDark ? 65 : 45;           // alternate lightness to increase contrast
    const l2 = isDark ? 55 : 55;           // tweak if you want stronger alternation
    return Array.from({ length: count }, (_, i) => {
      const hue = (i * 137.508) % 360;     // golden angle
      const l = i % 2 ? l1 : l2;
      return `hsl(${hue}, ${s}%, ${l}%)`;
    });
  };

  const useSeriesStyles = (needed, isDark) => {
    return useMemo(() => {
      const colors = buildPalette(Math.min(needed, 48), isDark); // 48 well-separated hues
      return { colors, dashes: DASHES };
    }, [needed, isDark]);
  };

  const playersOrdered = useMemo(
    () => filteredPlayers.map(p => `${p.first_name} ${p.last_name}`).sort((a,b) => a.localeCompare(b)),
    [filteredPlayers]
  );

  const pickerPlayers = useMemo(
    () =>
      filteredPlayers.map((p) => ({
        name: `${p.first_name} ${p.last_name}`,
        position: p.position || "N/A",
      })),
    [filteredPlayers]
  );

  const { colors, dashes } = useSeriesStyles(48, isDarkMode); // 48 colors * 2+ dashes = 96+ unique styles

  const getStyleForPlayer = (name) => {
    const i = playersOrdered.indexOf(name);
    const color = colors[i % colors.length];
    const dash = dashes[Math.floor(i / colors.length) % dashes.length];
    return { color, dash };
  };

  const clampRange = (min, max, a, b) => {
    // retourne [lo, hi] borné et trié
    let lo = Math.max(min, Math.min(a, b));
    let hi = Math.min(max, Math.max(a, b));
    return [lo, hi];
  };

  const processChartData = (metric) => {
    let chartData = [];

    const activeFilteredPlayers = filteredPlayers.filter((p) => {
      const name = `${p.first_name} ${p.last_name}`;
      return activePlayers[name];
    });
    // Fallback : si personne n'est actif (cas extrême), on retombe sur tous les filtrés
    const basePlayersForStats = activeFilteredPlayers.length
      ? activeFilteredPlayers
      : filteredPlayers;


    // 1) Full timeline for the selected period (week / month / season / custom)
    const allDates = buildChartDateAxis(periodRange);

    // Fallback: derive axis from available session dates if range is unavailable
    if (!allDates.length) {
      const datesWithData = [...new Set(
        filteredPlayers.flatMap((player) =>
          (player.weeklyHistory || []).map((session) => session.day)
        )
      )].sort((a, b) => {
        const dateA = new Date(a.split("/").reverse().join("-"));
        const dateB = new Date(b.split("/").reverse().join("-"));
        return dateA - dateB;
      });

      if (datesWithData.length < 2) {
        allDates.push(...datesWithData);
      } else {
        const parse = (d) => {
          const [dd, mm, yyyy] = d.split("/");
          return new Date(yyyy, mm - 1, dd);
        };
        const fmt = (d) =>
          `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
        const start = parse(datesWithData[0]);
        const end = parse(datesWithData[datesWithData.length - 1]);
        const cur = new Date(start);
        while (cur <= end) {
          allDates.push(fmt(cur));
          cur.setDate(cur.getDate() + 1);
        }
      }
    }

    allDates.forEach((date) => {
      const dataPoint = { date };

      filteredPlayers.forEach((player) => {
        const session = (player.weeklyHistory || []).find(s => s.day === date);
        if (!session) return;

        if (["acceleration_threshold", "deceleration_threshold", "sprint_threshold"].includes(metric)) {
          const accelArray = session.acceleration || [];
          const velArray   = session.velocity || [];

          const keyFor = (action) => {
            if (mode === "number")   return `${action}_number`;
            if (mode === "time")     return `${action}_duration`;
            return `${action}_distance`;
          };

          const fullName = `${player.first_name} ${player.last_name}`;
          let val = null;

          if (metric === "acceleration_threshold") {
            const ref = effectiveTeamRefs.teamAccelMax; // m/s²

            const [minPct, maxPct] = clampRange(0, 130, accelPctRange[0], accelPctRange[1]);
            const minT = (minPct / 100) * ref;
            const maxT = (maxPct / 100) * ref;

            const iLo = accelArray.findIndex(a => a >= minT);
            const iHi = accelArray.findIndex(a => a >= maxT);

            if (iLo !== -1) {
              const arr   = session[keyFor("acceleration")] || [];
              const loVal = arr[iLo] ?? null;
              const hiVal = (iHi !== -1 ? arr[iHi] : null);
              if (loVal != null) {
                val = (hiVal != null ? (loVal - hiVal) : loVal);
                val = typeof val === "number" ? parseFloat(val.toFixed(2)) : null;
              }
            }
          }


          if (metric === "deceleration_threshold") {
            const refMag = effectiveTeamRefs.teamDecelMagMax; // m/s² (magnitude)

            const [loPct, hiPct] = clampRange(0, 130, decelPctRange[0], decelPctRange[1]);

            const loMag = (loPct / 100) * refMag; // lower threshold (magnitude)
            const hiMag = (hiPct / 100) * refMag; // upper threshold (magnitude)

            // negative thresholds
            const maxNeg = -loMag; // ex: -2
            const minNeg = -hiMag; // ex: -6

            const minMag = -minNeg; // hiMag
            const maxMag = -maxNeg; // loMag

            const iLo = accelArray.findIndex(a => a >= maxMag); // >= loMag
            const iHi = accelArray.findIndex(a => a >= minMag); // >= hiMag

            if (iLo !== -1) {
              const arr   = session[keyFor("deceleration")] || [];
              const loVal = arr[iLo] ?? null;
              const hiVal = (iHi !== -1 ? arr[iHi] : null);
              if (loVal != null) {
                val = (hiVal != null ? (loVal - hiVal) : loVal);
                val = typeof val === "number" ? parseFloat(val.toFixed(2)) : null;
              }
            }
          }


          if (metric === "sprint_threshold") {
            const vmaMax = effectiveTeamRefs.teamVmaMax; // km/h

            // range in %
            const [minPct, maxPct] = clampRange(0, 130, sprintPctRange[0], sprintPctRange[1]); // allows >100%
            const minKmH = (minPct / 100) * vmaMax;
            const maxKmH = (maxPct / 100) * vmaMax;

            const minMs = minKmH / 3.6;
            const maxMs = maxKmH / 3.6;

            const iLo = velArray.findIndex(v => v >= minMs);
            const iHi = velArray.findIndex(v => v >= maxMs);

            if (iLo !== -1) {
              const arr   = session[keyFor("sprint")] || [];
              const loVal = arr[iLo] ?? null;
              const hiVal = (iHi !== -1 ? arr[iHi] : null);
              if (loVal != null) {
                val = (hiVal != null ? (loVal - hiVal) : loVal);
                val = typeof val === "number" ? parseFloat(val.toFixed(2)) : null;
              }
            }
          }


          dataPoint[fullName] = val;
        } else {  
        let value = null;
if (session) {
  if (metric === "microcycle") {
    value = session[useMD60 ? "microcycle60" : "microcycle"];
  } else if (metric === "trainability") {
    if (trainabilityScope === "global") {
      value = session[trainabilityPeriod === "30" ? "trainability_n_30" : "trainability_n_60"];
    } else {
      const nd = trainabilityScope === "day" ? 1 : trainabilityScope === "week" ? 7 : 28;
      value = session[`trainability_nd${nd}_${trainabilityPeriod}`];
    }
  } else {
    value = session[metric];
  }
}
    
      const fullName = `${player.first_name} ${player.last_name}`;
      dataPoint[fullName] = value;
        }
      });

      chartData.push(dataPoint);
    });

    // Cumulative if needed
    if (["intensity_index", "mechanical_load_index"].includes(metric) && cumulative) {
      const cumulativeSums = {};
      chartData = chartData.map((point) => {
        const newPoint = { date: point.date };
        filteredPlayers.forEach((player) => {
          const name = `${player.first_name} ${player.last_name}`;
          const current = typeof point[name] === "number" ? point[name] : 0;
          if (!cumulativeSums[name]) cumulativeSums[name] = 0;
          cumulativeSums[name] += current;
          newPoint[name] = parseFloat(cumulativeSums[name].toFixed(1));
        });
        return newPoint;
      });
    }

    // Median / standard deviation / out-of-band flags
    chartData = chartData.map((point) => {
      const values = basePlayersForStats
        .map((p) => {
          const name = `${p.first_name} ${p.last_name}`;
          const v = point[name];
          return typeof v === "number" && !Number.isNaN(v) ? v : null;
        })
        .filter((v) => v != null);

      if (!values.length) return point;

      const sortedVals = [...values].sort((a, b) => a - b);
      const mid = Math.floor(sortedVals.length / 2);
      const median = sortedVals.length % 2
        ? sortedVals[mid]
        : (sortedVals[mid - 1] + sortedVals[mid]) / 2;

      const variance = values.reduce((acc, v) => acc + (v - median) ** 2, 0) / values.length;
      const std = Math.sqrt(variance);

      const bandLow  = median - std;
      const bandHigh = median + std;
      const bandRange = bandHigh - bandLow;

      const rawSpan = bandHigh - bandLow || 1;
      const epsilon = rawSpan * 0.02;

      const effectiveLow  = bandLow  - epsilon;
      const effectiveHigh = bandHigh + epsilon;

      const newPoint = {
        ...point,
        median,
        bandLow,
        bandHigh,
        bandBase: bandLow,
        bandRange: bandRange,
      };

      filteredPlayers.forEach((p) => {
        const name = `${p.first_name} ${p.last_name}`;
        const v = point[name];

        const isOut =
          typeof v === "number" &&
          !Number.isNaN(v) &&
          (v < effectiveLow || v > effectiveHigh);

        newPoint[`${name}__isOut`]   = isOut;
        newPoint[`${name}__outDot`]  = null;  // on remplira après
      });

      return newPoint;
    });

    // 🔹 Build colored segments + out-of-band dots
    const coloredKeysByPlayer = {};

    filteredPlayers.forEach((p) => {
      const name = `${p.first_name} ${p.last_name}`;
      coloredKeysByPlayer[name] = [];
      let segmentIndex = 0;
      let currentKey = null;
      let inSegment = false;

      for (let i = 0; i < chartData.length; i++) {
        const point = chartData[i];
        const v = point[name];
        const currIsValid = typeof v === "number" && !Number.isNaN(v);
        const currIsOut = !!point[`${name}__isOut`];

        // Dots : tous les points hors bande auront une dot
        if (currIsValid && currIsOut) {
          point[`${name}__outDot`] = v;
        }

        if (i === 0) continue;

        const prevPoint = chartData[i - 1];
        const prevV = prevPoint[name];
        const prevIsValid = typeof prevV === "number" && !Number.isNaN(prevV);
        const prevIsOut = !!prevPoint[`${name}__isOut`];

        // Règle : colorer le segment si au moins un des 2 points est out-of-band
        const shouldColorEdge = prevIsValid && currIsValid && (prevIsOut || currIsOut);

        if (shouldColorEdge) {
          if (!inSegment) {
            currentKey = `${name}__out_${segmentIndex++}`;
            coloredKeysByPlayer[name].push(currentKey);
            inSegment = true;
          }
          // On pose les valeurs pour ce segment uniquement sur cette clé
          if (prevPoint[currentKey] == null) prevPoint[currentKey] = prevV;
          if (point[currentKey] == null) point[currentKey] = v;
        } else {
          inSegment = false;
          currentKey = null;
        }
      }
    });

    // Y domain
    const numericValues = chartData.flatMap((point) =>
      basePlayersForStats
        .map((p) => point[`${p.first_name} ${p.last_name}`])
        .filter((v) => typeof v === "number" && !Number.isNaN(v))
    );

    const minRaw = numericValues.length ? Math.min(...numericValues) : 0;
    const maxRaw = numericValues.length ? Math.max(...numericValues) : 1;

    const minValue = Math.round(minRaw * 0.95);
    const maxValue = Math.round(maxRaw * 1.05);

    return {
      chartData,
      players: filteredPlayers.map(p => `${p.first_name} ${p.last_name}`),
      minValue,
      maxValue,
      coloredKeysByPlayer,
    };
  };


  return (
    <div className={`p-6 min-h-screen ${isDarkMode ? "bg-gray-900 text-white" : "bg-gray-100 text-black"}`}>
      <h1 className="text-2xl font-semibold text-center mb-6">{t('charts.title')}</h1>
      {referenceError && (
        <div
          className={`mb-4 rounded-lg border px-3 py-2 text-xs ${
            isDarkMode
              ? "border-amber-500/40 bg-amber-500/10 text-amber-300"
              : "border-amber-300 bg-amber-50 text-amber-700"
          }`}
        >
          {referenceError}
        </div>
      )}

      {/* Period selector + display mode */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-4">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex items-center gap-2">
            <label className="text-sm opacity-80">{t('charts.period')}:</label>
            <select
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              className="px-3 py-2 rounded-md border border-blue-500 bg-white text-black shadow-sm hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
            >
              <option value="week">{t('charts.week')}</option>
              <option value="month">{t('charts.month')}</option>
              <option value="season">{t('charts.season')}</option>
              <option value="custom">{t('charts.custom')}</option>
            </select>
          </div>

          {/* Toggle points / curves */}
          <div className="flex items-center gap-2">
            <span className="text-sm opacity-80">{t('charts.display')}:</span>
            <div
              className={`inline-flex text-xs rounded-lg border overflow-hidden ${
                isDarkMode
                  ? "border-gray-700 bg-gray-900"
                  : "border-gray-300 bg-white"
              }`}
            >
              <button
                type="button"
                onClick={() => setDisplayMode("points")}
                className={`px-3 py-1.5 transition ${
                  displayMode === "points"
                    ? "bg-indigo-600 text-white"
                    : isDarkMode
                      ? "text-gray-200 hover:bg-gray-800"
                      : "text-gray-700 hover:bg-gray-100"
                }`}
              >
                {t('charts.points')}
              </button>
              <button
                type="button"
                onClick={() => setDisplayMode("curve")}
                className={`px-3 py-1.5 border-l ${
                  isDarkMode ? "border-gray-700" : "border-gray-300"
                } transition ${
                  displayMode === "curve"
                    ? "bg-indigo-600 text-white"
                    : isDarkMode
                      ? "text-gray-200 hover:bg-gray-800"
                      : "text-gray-700 hover:bg-gray-100"
                }`}
              >
                {t('charts.curves')}
              </button>
            </div>
          </div>
        </div>

        {period === "custom" && (
          <div className="flex flex-wrap items-end gap-2">
            <div className="flex flex-col">
              <label className="text-xs mb-1">{t('charts.start')}</label>
              <input
                type="date"
                value={startDate}
                max={endDate || todayIso}
                onChange={(e) => setStartDate(e.target.value)}
                className="px-3 py-2 rounded-md border bg-white text-black shadow-sm focus:outline-none"
              />
            </div>
            <div className="flex flex-col">
              <label className="text-xs mb-1">{t('charts.end')}</label>
              <input
                type="date"
                value={endDate}
                min={startDate || undefined}
                max={todayIso}
                onChange={(e) => setEndDate(e.target.value)}
                className="px-3 py-2 rounded-md border bg-white text-black shadow-sm focus:outline-none"
              />
            </div>
            {/* UX tip: button to quickly swap dates if needed */}
            <button
              type="button"
              onClick={() => { if (startDate && endDate) { setStartDate(endDate); setEndDate(startDate); } }}
              className="px-3 py-2 rounded-md border border-gray-400 dark:border-gray-600 hover:bg-gray-200 dark:hover:bg-gray-800"
            >{t('charts.swap')}
            </button>
          </div>
        )}
      </div>

      {/* ---------------------- CENTRAL CONTENT ------------------------ */}
      {showLoading && (
        <div className="flex items-center justify-center py-8" role="status" aria-live="polite">
          <span
            className={`inline-block h-5 w-5 mr-2 rounded-full border-2 border-current border-t-transparent animate-spin ${
              isDarkMode ? "text-indigo-300" : "text-indigo-600"
            }`}
          />
          <span className={isDarkMode ? "text-gray-300" : "text-gray-600"}>{t("common.loading")}</span>
        </div>
      )}

      {!showLoading && !hasData && (
        <p className="text-center italic">
       {t('charts.no_data_period', { period, season })}
        </p>
      )}

      {!showLoading && hasData && (
        <>
          <div className="flex border-b mb-4 overflow-x-auto">
            {allMetrics.map(({ key, label }) => {
              let displayLabel = label;
              if (["acceleration_threshold", "deceleration_threshold", "sprint_threshold"].includes(key)) {
                if (mode === "distance") displayLabel = `${label} (m)`;
                else if (mode === "time") displayLabel = `${label} (s)`;
                else if (mode === "number") displayLabel = `${label} (number)`;
              }
              return (
                <motion.button
                  key={key}
                  className={`px-4 py-2 transition-all ${activeTab === key ? "border-b-2 border-blue-500 font-bold" : "text-gray-500 hover:text-blue-500"}`}
                  onClick={() => setActiveTab(key)}
                  whileHover={{ scale: 1.05 }}
                >
                  {displayLabel}
                </motion.button>
              );
            })}
          </div>

          {["intensity_index", "mechanical_load_index"].includes(activeTab) && (
            <div className="flex justify-end items-center mb-4">
              <label className="flex items-center cursor-pointer">
                <span className="mr-2 text-sm">{t('charts.cumul')}</span>
                <div className="relative">
                  <input type="checkbox" className="sr-only peer" checked={cumulative} onChange={() => setCumulative(!cumulative)} />
                  <div className="w-11 h-6 flex items-center bg-gray-600 rounded-full p-1 duration-300 ease-in-out peer-checked:bg-blue-500">
                    <div className={`bg-white w-4 h-4 rounded-full shadow-md transform duration-300 ease-in-out ${cumulative ? "translate-x-5" : ""}`} />
                  </div>
                </div>
              </label>
            </div>
          )}
{activeTab === "microcycle" && (
  <div className="flex items-center gap-3 mb-4">
    <label className="flex items-center cursor-pointer gap-2">
      <div className="relative">
        <input type="checkbox" className="sr-only peer" checked={useMD60} onChange={() => setUseMD60(!useMD60)} />
        <div className="w-11 h-6 flex items-center bg-gray-600 rounded-full p-1 duration-300 ease-in-out peer-checked:bg-blue-500">
          <div className={`bg-white w-4 h-4 rounded-full shadow-md transform duration-300 ease-in-out ${useMD60 ? "translate-x-5" : ""}`} />
        </div>
      </div>
      <span className="text-sm opacity-80">{t('charts.high_load_mode')}</span>
    </label>
    <div className="relative group flex items-center">
      <span className="w-4 h-4 rounded-full border border-gray-400 text-gray-400 text-[10px] flex items-center justify-center cursor-help">?</span>
      <div className="absolute left-6 top-1/2 -translate-y-1/2 z-10 hidden group-hover:block w-64 text-xs p-2 rounded shadow-lg bg-gray-800 text-gray-100 border border-gray-600">
        {t('charts.high_load_tooltip')}
      </div>
    </div>
  </div>
)}

{activeTab === "trainability" && (
  <div className="flex items-center gap-3 mb-4">
    <label className="flex items-center cursor-pointer gap-2">
      <div className="relative">
        <input type="checkbox" className="sr-only peer"
          checked={trainabilityPeriod === "60"}
          onChange={() => setTrainabilityPeriod(p => p === "30" ? "60" : "30")} />
        <div className="w-11 h-6 flex items-center bg-gray-600 rounded-full p-1 duration-300 ease-in-out peer-checked:bg-blue-500">
          <div className={`bg-white w-4 h-4 rounded-full shadow-md transform duration-300 ease-in-out ${trainabilityPeriod === "60" ? "translate-x-5" : ""}`} />
        </div>
      </div>
      <span className="text-sm opacity-80">{t('charts.high_load_mode')}</span>
    </label>

    <div className="relative group flex items-center">
      <span className="w-4 h-4 rounded-full border border-gray-400 text-gray-400 text-[10px] flex items-center justify-center cursor-help">?</span>
      <div className="absolute left-6 top-1/2 -translate-y-1/2 z-10 hidden group-hover:block w-64 text-xs p-2 rounded shadow-lg bg-gray-800 text-gray-100 border border-gray-600">
        {t('charts.high_load_tooltip')}
      </div>
    </div>

    <div className={`inline-flex text-xs rounded-lg border overflow-hidden ${isDarkMode ? "border-gray-700 bg-gray-900" : "border-gray-300 bg-white"}`}>
      {[
{ key: "global", label: t('charts.scope_global') },
{ key: "day",    label: t('charts.scope_day') },
{ key: "week",   label: t('charts.scope_week') },
{ key: "month",  label: t('charts.scope_month') },
      ].map(({ key, label }) => (
        <button
          key={key}
          type="button"
          onClick={() => setTrainabilityScope(key)}
          className={`px-3 py-1.5 border-l first:border-l-0 ${isDarkMode ? "border-gray-700" : "border-gray-300"} transition ${
            trainabilityScope === key
              ? "bg-indigo-600 text-white"
              : isDarkMode ? "text-gray-200 hover:bg-gray-800" : "text-gray-700 hover:bg-gray-100"
          }`}
        >
          {label}
        </button>
      ))}
    </div>
  </div>
)}
          {allMetrics.map(({ key, label, unit }) => {
            if (key !== activeTab) return null;
            const { chartData, players, minValue, maxValue, coloredKeysByPlayer } = processChartData(key);

            const sliderProps = (() => {
              if (key === "sprint_threshold") {
                const vma = effectiveTeamRefs.teamVmaMax; // km/h
                const [lo, hi] = sprintPctRange;

                const loKmH = (lo / 100) * vma;
                const hiKmH = (hi / 100) * vma;

                return {
                  range: sprintPctRange,
                  setRange: setSprintPctRange,
                  min: 0,
                  max: 130,
                  step: 1,
                  unit: "%",
                  subLabel: `Ref: Team max VMA = ${vma.toFixed(1)} km/h • Range ≈ ${loKmH.toFixed(1)} – ${hiKmH.toFixed(1)} km/h`,
                  referenceEditor: {
                    currentValue: vma,
                    unit: referenceMetaByMetric.sprint_threshold.unit,
                    step: referenceMetaByMetric.sprint_threshold.step,
                    isManual: parseNullableReference(referenceOverrides.team_vma_max) != null,
                    draftValue: referenceDrafts.team_vma_max,
                    saving: referenceSaveKey === "team_vma_max",
                    onDraftChange: (value) => updateReferenceDraft("team_vma_max", value),
                    onSave: () => saveReference("team_vma_max"),
                    onReset: () => resetReference("team_vma_max"),
                  },
                };
              }

              if (key === "acceleration_threshold") {
                const ref = effectiveTeamRefs.teamAccelMax; // m/s²
                const [lo, hi] = accelPctRange;

                const loVal = (lo / 100) * ref;
                const hiVal = (hi / 100) * ref;

                return {
                  range: accelPctRange,
                  setRange: setAccelPctRange,
                  min: 0,
                  max: 130,
                  step: 1,
                  unit: "%",
                  subLabel: `Ref: Team max accel = ${ref.toFixed(2)} m/s² • Range ≈ ${loVal.toFixed(2)} – ${hiVal.toFixed(2)} m/s²`,
                  referenceEditor: {
                    currentValue: ref,
                    unit: referenceMetaByMetric.acceleration_threshold.unit,
                    step: referenceMetaByMetric.acceleration_threshold.step,
                    isManual: parseNullableReference(referenceOverrides.team_accel_max) != null,
                    draftValue: referenceDrafts.team_accel_max,
                    saving: referenceSaveKey === "team_accel_max",
                    onDraftChange: (value) => updateReferenceDraft("team_accel_max", value),
                    onSave: () => saveReference("team_accel_max"),
                    onReset: () => resetReference("team_accel_max"),
                  },
                };
              }

              if (key === "deceleration_threshold") {
                const refMag = effectiveTeamRefs.teamDecelMagMax; // magnitude (m/s²)
                const [lo, hi] = decelPctRange;

                const loMag = (lo / 100) * refMag;
                const hiMag = (hi / 100) * refMag;

                // displayed as negative because decelerations
                return {
                  range: decelPctRange,
                  setRange: setDecelPctRange,
                  min: 0,
                  max: 130,
                  step: 1,
                  unit: "%",
                  subLabel: `Ref: Team max decel = -${refMag.toFixed(2)} m/s² • Range ≈ -${loMag.toFixed(2)} to -${hiMag.toFixed(2)} m/s²`,
                  referenceEditor: {
                    currentValue: refMag,
                    unit: referenceMetaByMetric.deceleration_threshold.unit,
                    step: referenceMetaByMetric.deceleration_threshold.step,
                    isManual: parseNullableReference(referenceOverrides.team_decel_mag_max) != null,
                    draftValue: referenceDrafts.team_decel_mag_max,
                    saving: referenceSaveKey === "team_decel_mag_max",
                    onDraftChange: (value) => updateReferenceDraft("team_decel_mag_max", value),
                    onSave: () => saveReference("team_decel_mag_max"),
                    onReset: () => resetReference("team_decel_mag_max"),
                  },
                };
              }

              return null;
            })();



            return (
              <div key={key} className="mb-8">
                <div className="flex flex-col xl:flex-row items-stretch gap-4">
                  {/* === Graph: takes all remaining space === */}
                  <div className="flex-1 min-w-0">
                    <ResponsiveContainer width="100%" height={420}>
                      <ComposedChart
                        key={periodRangeKey || period}
                        data={chartData}
                        margin={{ top: 10, right: 20, left: 0, bottom: 30 }}
                      >
                        <XAxis
                          dataKey="date"
                          angle={-45}
                          textAnchor="end"
                          height={70}
                          interval={period === "season" ? 3 : period === "month" ? 2 : 0}
                          tickFormatter={(value) => {
                            if (period === "season" || period === "month" || period === "custom") {
                              const [day, month] = value.split("/").slice(0, 2);
                              return `${day}/${month}`;
                            }
                            return value;
                          }}
                        />
                        <YAxis
                          domain={[minValue, maxValue]}
                          tickFormatter={(v) => (v != null ? v.toFixed(1) : "")}
                        />
                        <Tooltip
                          content={({ active, payload, label }) => {
                            if (!active || !payload?.length) return null;
                            let periodLabel = "";
                            for (let player of filteredPlayers) {
                              const session = (player.weeklyHistory || []).find(s => s.day === label);
                              if (session) { periodLabel = session.period; break; }
                            }
                            const matchOpponent = matchDateSet.get(label);
                            return (
                              <div className="bg-white dark:bg-gray-800 text-gray-800 dark:text-white p-3 border border-gray-300 dark:border-gray-600 rounded shadow-md">
                                <p className="font-semibold text-sm mb-2">{label} {periodLabel ? `- ${periodLabel}` : ""}</p>
                                {matchOpponent !== undefined && (
                                  <div className="flex items-center gap-1.5 text-xs mb-2 px-1.5 py-1 rounded bg-amber-500/10 text-amber-600 dark:text-amber-400 font-medium">
                                    <span>▼</span>
                                    <span>{t('charts.match_day')}{matchOpponent ? ` — ${matchOpponent}` : ""}</span>
                                  </div>
                                )}
                                {payload.map((entry, idx) => (
                                  <div key={idx} className="flex items-center text-sm mb-1">
                                    <span className="inline-block w-3 h-3 rounded-full mr-2" style={{ backgroundColor: entry.color }} />
                                    <span className="mr-2">{entry.name}:</span>
                                    <span className="font-medium">{entry.value}</span>
                                  </div>
                                ))}
                              </div>
                            );
                          }}
                        />
                        <Legend wrapperStyle={{ display: "none" }} className="hidden md:block" />

                        <XAxis
                          dataKey="date"
                          angle={0}
                          tickMargin={8}
                          height={40}
                          interval={period === "season" ? 3 : period === "month" ? 2 : 0}
                          tickFormatter={(value) => {
                            if (period === "season" || period === "month" || period === "custom") {
                              const [day, month] = value.split("/").slice(0, 2);
                              return `${day}/${month}`;
                            }
                            return value;
                          }}
                        />

                        {/* 🔍 ZOOM / DÉZOOM SUR L’AXE X */}
                        <Brush
                          dataKey="date"
                          height={32}
                          travellerWidth={12}
                          stroke={isDarkMode ? "#4f46e5" : "#6366f1"} // indigo-600-ish
                          fill={isDarkMode ? "rgba(79,70,229,0.15)" : "rgba(129,140,248,0.16)"} // indigo/indigo-400 soft
                        >
                          {/* Mini-preview of median to recall the main graph */}
                          <LineChart data={chartData}>
                            <Line
                              type="monotone"
                              dataKey="median"
                              stroke={isDarkMode ? "#e5e7eb" : "#111827"} // gris clair / presque noir
                              strokeWidth={1.3}
                              dot={false}
                              isAnimationActive={false}
                            />
                          </LineChart>
                        </Brush>

                        {/* Match day indicators */}
                        {chartData
                          .filter(d => matchDateSet.has(d.date))
                          .map(d => (
                            <ReferenceLine
                              key={`match-${d.date}`}
                              x={d.date}
                              stroke={isDarkMode ? "rgba(251,191,36,0.5)" : "rgba(217,119,6,0.4)"}
                              strokeDasharray="4 3"
                              strokeWidth={1.4}
                              ifOverflow="extendDomain"
                              label={{
                                value: "▼",
                                position: "top",
                                fontSize: 11,
                                offset: 2,
                                fill: isDarkMode ? "#fbbf24" : "#d97706",
                              }}
                            />
                          ))
                        }

                        <Line
                          type="monotone"
                          dataKey="median"
                          stroke={isDarkMode ? "#ffffff" : "#000000"}
                          strokeWidth={2}
                          dot={false}
                          isAnimationActive={false}
                          name={t('charts.team_median')}
                        />
                        {displayMode === "points" && (
                          <>
                            <Line
                              type="monotone"
                              dataKey="bandLow"
                              stroke={
                                isDarkMode
                                  ? "rgba(148,163,184,0.9)"
                                  : "rgba(148,163,184,0.9)"
                              }
                              strokeWidth={1}
                              strokeDasharray="4 4"
                              dot={false}
                              isAnimationActive={false}
                              name="-1σ"
                            />
                            <Line
                              type="monotone"
                              dataKey="bandHigh"
                              stroke={
                                isDarkMode
                                  ? "rgba(148,163,184,0.9)"
                                  : "rgba(148,163,184,0.9)"
                              }
                              strokeWidth={1}
                              strokeDasharray="4 4"
                              dot={false}
                              isAnimationActive={false}
                              name="+1σ"
                            />

                            {/* Filled area = standard deviation range */}
                            <Area
                              type="monotone"
                              dataKey="bandBase"
                              stackId="band"
                              stroke="none"
                              fill="transparent"
                              isAnimationActive={false}
                            />
                            <Area
                              type="monotone"
                              dataKey="bandRange"
                              stackId="band"
                              stroke="none"
                              fill={
                                isDarkMode
                                  ? "rgba(129,140,248,0.35)" // indigo-400-ish
                                  : "rgba(129,140,248,0.25)"
                              }
                              isAnimationActive={false}
                            />
                          </>
                        )}


                        {/* Player lines: part IN range = gray, out of range = colored */}
                        {/* Curve mode: current behavior, with colored segments */}
                        {displayMode === "curve" &&
                          players
                            .filter((player) => activePlayers[player])
                            .map((player) => {
                              const { color, dash } = getStyleForPlayer(player);

                              return (
                                <Line
                                  key={player}
                                  type="monotone"
                                  dataKey={player}
                                  stroke={color}
                                  strokeWidth={1.8}
                                  strokeDasharray={dash}
                                  dot={{
                                    r: 2.5,
                                    stroke: color,
                                    fill: isDarkMode ? "#111827" : "#ffffff",
                                  }}
                                  activeDot={{ r: 4 }}
                                  connectNulls={false}
                                  isAnimationActive={false}
                                  name={player}
                                />
                              );
                            })}

                        {/* Points mode: only out-of-band points, no curves */}
                        {displayMode === "points" &&
                          players
                            .filter((player) => activePlayers[player])
                            .map((player) => {
                              const { color } = getStyleForPlayer(player);
                              return (
                                <Line
                                  key={`${player}__points`}
                                  type="monotone"
                                  dataKey={`${player}__outDot`}
                                  stroke="none"
                                  dot={{
                                    r: 3,
                                    stroke: color,
                                    fill: isDarkMode ? "#111827" : "#ffffff",
                                  }}
                                  activeDot={{ r: 5 }}
                                  isAnimationActive={false}
                                  name={player}
                                />
                              );
                            })}

                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>

                  {/* === Bounded sidebar: PlayerPicker + (optionally) Sliders === */}
                  <SideControls
                    showSliders={!!sliderProps}
                    picker={
                      <PlayerPicker
                        compact
                        players={pickerPlayers}
                        statusByName={playerRiskStatusByName}
                        activeMap={activePlayers}
                        onToggle={(name) =>
                          setActivePlayers((prev) => ({ ...prev, [name]: !prev[name] }))
                        }
                        onAll={() =>
                          setActivePlayers(
                            Object.fromEntries(pickerPlayers.map((p) => [p.name, true]))
                          )
                        }
                        onNone={() =>
                          setActivePlayers(
                            Object.fromEntries(pickerPlayers.map((p) => [p.name, false]))
                          )
                        }
                        onInvert={() =>
                          setActivePlayers(
                            Object.fromEntries(
                              pickerPlayers.map((p) => [p.name, !activePlayers[p.name]])
                            )
                          )
                        }
                        getStyleForPlayer={getStyleForPlayer}
                        isDark={isDarkMode}
                      />
                    }
                    sliders={sliderProps ? (
                      <CompactThreshold
                        range={sliderProps.range}
                        setRange={sliderProps.setRange}
                        min={sliderProps.min}
                        max={sliderProps.max}
                        unit={sliderProps.unit}
                        mode={mode}
                        setMode={setMode}
                        isDarkMode={isDarkMode}
                        subLabel={sliderProps.subLabel}
                        referenceEditor={sliderProps.referenceEditor}
                      />
                    ) : null}
                    isDarkMode={isDarkMode}
                  />
                </div>
              </div>

            );
          })}
        </>
      )}
    </div>
  );
}
